"use client";

import { Text } from "@medusajs/ui";
import LocalizedClientLink from "@modules/common/components/localized-client-link";
import Thumbnail from "../thumbnail";
import { BiTransfer } from "react-icons/bi";
import { useState, useCallback, useMemo } from "react";
import { addToCart } from "@lib/data/cart";
import { useCart } from "@context/cart-context";
import { useAnalytics } from "@context/analytics-context";
import { mapProductToItem } from "@lib/analytics/mappers";
import { getSearchAttribution, SearchAttribution } from "@lib/search/attribution";
import QuickAddDrawer, { extractVariantOptionValues, getVariantLabel } from "./quick-add-drawer";
import { useCartDrawer } from "@context/cart-drawer-context";
import { usePaymentMethods } from "@context/payment-methods-context";
import { getVariantIdentifier } from "@lib/util/variants";
import Spinner from "@modules/common/icons/spinner";
import MoneyClient from "../../../../components/MoneyClient";
import { normalizeCurrency, toCents } from "@lib/currency";
import { STORE_BASE_CURRENCY } from "@lib/config";
import RatingStars from "@modules/reviews/components/rating-stars";
import { capitalizeFirstLetter } from "@lib/util/text";
import { resolveProductBrandSlug, resolveProductHref } from "@lib/util/product-route";
import { ProductStockLabel } from "./stock-label";
export default function ProductPreview({
  product,
  isFeatured,
  region,
  imagePriority = false,
  searchAttribution: searchAttributionOverride,
  onProductClick
}: {
  product: any;
  isFeatured?: boolean;
  region: any;
  imagePriority?: boolean;
  searchAttribution?: SearchAttribution | null;
  onProductClick?: () => void;
}) {
  // const pricedProduct = await listProducts({
  //   regionId: region.id,
  //   queryParams: { id: [product.id!] },
  // }).then(({ response }) => response.products[0])

  // if (!pricedProduct) {
  //   return null
  // }

  const originalPrice = typeof product.originalPrice === "number" ? product.originalPrice : product.price || 0;
  const discountedPrice = typeof product.discountedPrice === "number" ? product.discountedPrice : null;
  const hasDiscount = discountedPrice !== null && discountedPrice > 0 && discountedPrice < originalPrice;
  const finalPrice = hasDiscount ? discountedPrice : originalPrice;
  const computedPercent = hasDiscount ? Math.round((1 - finalPrice / originalPrice) * 100) : undefined;
  const displayPercent = typeof product.discount === "number" && product.discount > 0 ? product.discount : computedPercent;
  const {
    wire
  } = usePaymentMethods();
  const productCurrency = normalizeCurrency(typeof product?.currency === "string" ? product.currency : STORE_BASE_CURRENCY, STORE_BASE_CURRENCY);
  const {
    showTransferPrice,
    transferPrice,
    transferPercent
  } = useMemo(() => {
    if (wire && wire.enabled && wire.discountActive && wire.discountPercent > 0) {
      const percent = wire.discountPercent;
      const price = finalPrice * (1 - percent / 100);
      return {
        showTransferPrice: true,
        transferPrice: price,
        transferPercent: percent
      };
    }
    return {
      showTransferPrice: false,
      transferPrice: finalPrice,
      transferPercent: 0
    };
  }, [finalPrice, wire]);
  const reviewSummary = product?.reviewSummary;
  const averageRating = typeof reviewSummary?.averageOverall === "number" && reviewSummary.averageOverall > 0 ? reviewSummary.averageOverall : typeof reviewSummary?.bayesianAverageOverall === "number" ? reviewSummary.bayesianAverageOverall : 0;
  const reviewCount = typeof reviewSummary?.totalReviews === "number" ? reviewSummary.totalReviews : 0;
  const displayProductName = capitalizeFirstLetter(product?.name);
  const variants: any[] = useMemo(() => Array.isArray(product?.variants) ? product.variants : [], [product?.variants]);
  const defaultVariant = variants[0];
  const hasVariantPriceSpread = useMemo(() => {
    if (variants.length <= 1) {
      return false;
    }
    const prices = new Set<number>();
    variants.forEach((variant: any) => {
      const candidates = [variant?.price, variant?.calculated_price, variant?.calculatedPrice, variant?.amount, variant?.unit_price];
      const numeric = candidates.map(candidate => Number(candidate)).find(value => Number.isFinite(value));
      if (typeof numeric === "number") {
        prices.add(numeric);
      }
    });
    return prices.size > 1;
  }, [variants]);
  const requiresVariantSelection = useMemo(() => {
    const optionsCount = Array.isArray(product?.options) ? product.options.length : 0;
    if (variants.length <= 1) {
      return false;
    }
    return optionsCount > 0 || hasVariantPriceSpread;
  }, [hasVariantPriceSpread, product?.options, variants.length]);
  const [isAdding, setIsAdding] = useState(false);
  const [isVariantDrawerOpen, setVariantDrawerOpen] = useState(false);
  const {
    setCart,
    refreshCart
  } = useCart();
  const analytics = useAnalytics();
  const {
    open: openCartDrawer
  } = useCartDrawer();
  const resolvedSearchAttribution = useMemo(() => {
    if (searchAttributionOverride) return searchAttributionOverride;
    const productId = String(product?._id || product?.id || "");
    return getSearchAttribution(productId || undefined, product?.slug);
  }, [product?._id, product?.id, product?.slug, searchAttributionOverride]);
  const handleAddToCart = useCallback(async (variant_0?: any) => {
    if (!product?.slug) return;
    const productId_0 = String(product?._id || product?.id || "").trim();
    setIsAdding(true);
    try {
      const variantOptionValues = extractVariantOptionValues(variant_0);
      const variantId = getVariantIdentifier(variant_0);
      const updatedCart = await addToCart({
        productId: productId_0 || undefined,
        productSlug: product.slug,
        quantity: 1,
        variantOptionValues,
        variantId,
        searchAttribution: resolvedSearchAttribution ? {
          ...resolvedSearchAttribution,
          productId: resolvedSearchAttribution.productId || String(product?._id || product?.id || ""),
          platform: resolvedSearchAttribution.platform || "web"
        } : undefined
      });
      if (updatedCart) {
        setCart(updatedCart);
      } else {
        await refreshCart();
      }
      const {
        item,
        currency,
        unitPrice
      } = mapProductToItem(product, {
        quantity: 1,
        variant: variant_0 || undefined,
        fallbackCurrency: product?.currency || region?.currency_code
      });
      analytics.trackAddToCart({
        items: [item],
        currency,
        value: typeof unitPrice === "number" ? unitPrice : undefined
      });
      openCartDrawer({
        title: displayProductName || product?.name,
        image: product?.thumbnail,
        variantLabel: variant_0 ? getVariantLabel(product, variant_0) : undefined,
        quantity: 1
      });
    } catch (error) {
      console.error("Failed to add item to cart", error);
      await refreshCart();
    } finally {
      setIsAdding(false);
      setVariantDrawerOpen(false);
    }
  }, [analytics, openCartDrawer, product, refreshCart, resolvedSearchAttribution, region?.currency_code, setCart]);
  const handleQuickAdd = useCallback(() => {
    if (requiresVariantSelection) {
      setVariantDrawerOpen(true);
      return;
    }
    if (defaultVariant) {
      void handleAddToCart(defaultVariant);
    } else {
      void handleAddToCart();
    }
  }, [defaultVariant, handleAddToCart, requiresVariantSelection]);

  // Build the new route with brand
  const brandSlug = resolveProductBrandSlug(product);
  const productRoute = resolveProductHref(product);
  return <div data-testid="product-wrapper" className="flex flex-col">
      <div className="relative">
        <LocalizedClientLink href={productRoute} onClick={onProductClick} className="group block">
          <div className="relative">
            <Thumbnail thumbnail={product.thumbnail} videoUrl={product.videoUrl} images={product.images} size="full" isFeatured={isFeatured} priority={imagePriority} />
            {hasDiscount && displayPercent ? <div className="absolute right-4 top-4 flex h-[62px] w-[62px] items-center justify-center bg-lanamara text-[12px] font-semibold uppercase tracking-wide text-white">
                {displayPercent}% OFF
              </div> : null}
          </div>
        </LocalizedClientLink>

        <button type="button" onClick={handleQuickAdd} disabled={isAdding} aria-label="Agregar rápidamente al carrito" className="absolute bottom-4 right-4 flex h-6 w-6 items-center justify-center rounded-none bg-white text-2xl font-light text-black shadow-lg transition hover:scale-105 disabled:cursor-not-allowed">
          {isAdding ? <Spinner size={12} /> : <span className="font-lealti-light text-md">+</span>}
        </button>
      </div>

      <div className="flex flex-col gap-1 mt-2 justify-between">
        <Text className="text-sm font-lealti text-black" data-testid="product-brand">
          {product.brand?.name ? <LocalizedClientLink href={`/${brandSlug}`} className="text-sm font-lealti text-black">
              {product.brand.name}
            </LocalizedClientLink> : null}
        </Text>

        <LocalizedClientLink href={productRoute} className="text-sm md:text-lg font-lealti-light text-black" data-testid="product-title">
          {displayProductName || product.name}
        </LocalizedClientLink>

        {reviewCount > 0 && <div className="flex items-center gap-2 text-xs text-gray-500">
            <RatingStars value={averageRating} size="sm" readOnly />
            <span>
              {averageRating.toFixed(1)} ({reviewCount})
            </span>
          </div>}

        {hasDiscount ? <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-lealti-light text-gray-500 line-through">
                <MoneyClient amountCents={toCents(originalPrice)} fromCurrency={productCurrency} />
              </span>
              {displayPercent ? <div className="flex h-[32px] w-[62px] items-center justify-center bg-lanamara text-[9px] font-lealti-light uppercase tracking-wide text-white">
                  {displayPercent}% OFF
                </div> : null}
              <span className="text-md md:text-lg font-lealti-light text-black">
                <MoneyClient amountCents={toCents(finalPrice)} fromCurrency={productCurrency} />
              </span>
            </div>
          </div> : <div className="flex flex-col gap-2">
            <span className="text-base md:text-lg font-lealti-light text-black">
              <MoneyClient amountCents={toCents(finalPrice)} fromCurrency={productCurrency} />
            </span>
          </div>}
        <ProductStockLabel product={product} />
        {showTransferPrice && <div className="flex items-center gap-x-2">
            <div className="flex items-center gap-x-2 rounded-xs border border-black bg-white p-1">
              <BiTransfer className="h-3 w-3 text-black" />
              <span className=" text-[10px] font-lealti-light tracking-[0.05em] text-black">TRANSFERENCIA</span>
            </div>
            <span className="text-sm font-lealti-light text-black">
              <MoneyClient amountCents={toCents(transferPrice)} fromCurrency={productCurrency} />
            </span>
            {transferPercent > 0 && <span className="text-[10px] font-lealti-light text-emerald-700">
                {transferPercent}% OFF
              </span>}
          </div>}
      </div>
      <QuickAddDrawer product={product} open={isVariantDrawerOpen} isLoading={isAdding} onClose={() => setVariantDrawerOpen(false)} onSelectVariant={variant_1 => void handleAddToCart(variant_1)} />
    </div>;
}